import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function RequireAuth({ children }: { children: ReactNode }) {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    // Xóa phiên đăng nhập cục bộ khi refresh token đã hết hạn / bị thu hồi,
    // tránh lặp lại lỗi "Invalid Refresh Token" mỗi lần tải trang.
    const clearStaleSession = async () => {
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // Bỏ qua lỗi khi dọn dẹp phiên.
      }
    };

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setAuthed(!!data?.session);
        setChecking(false);
      })
      .catch(() => {
        // Refresh token không còn hợp lệ -> dọn phiên và đưa về trang đăng nhập.
        clearStaleSession().finally(() => {
          if (!mounted) return;
          setAuthed(false);
          setChecking(false);
        });
      });

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'SIGNED_OUT') {
        setAuthed(false);
        setChecking(false);
      } else if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        setAuthed(!!session);
        setChecking(false);
      }
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="flex items-center gap-3 text-foreground-600">
          <i className="ri-loader-4-line text-2xl animate-spin text-primary-400"></i>
          <span className="text-sm">Đang tải...</span>
        </div>
      </div>
    );
  }

  if (!authed) {
    return <Navigate to={`/dang-nhap?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
}